// Campaigns API client (Faz 2). `/api/v1/campaigns/...`, ActionResponse envelope.
// create → CampaignDtoActionResponse (entity); update/workflow → Boolean (re-fetch).

import { http, unwrap, unwrapList, type ActionResponse, type PaginationInfo } from './http';
import {
  campaignStatusFromNumber,
  campaignStatusToNumber,
  type Campaign,
  type CampaignListItem,
  type CampaignListQuery,
  type CampaignVersion,
  type CampaignWriteInput,
} from '@features/campaigns/types';

interface CampaignListItemDto {
  id: string;
  title: string | null;
  slug: string | null;
  excerpt: string | null;
  categoryId: string | null;
  coverImageUrl: string | null;
  startDate: string | null;
  endDate: string | null;
  status: number;
  publishedAt: string | null;
  updatedAt: string;
  languageId?: string | null;
  groupId?: string | null;
  variantLanguageCodes?: string[] | null;
}

interface CampaignDto extends CampaignListItemDto {
  body: string | null;
  ctaLink: string | null;
  metaTitle: string | null;
  metaDescription: string | null;
  canonicalTag: string | null;
  customCode: string | null;
  keywords: string | null;
  allowIndexing: boolean;
  publishDate: string | null;
  createdAt: string;
}

interface CampaignVersionDto {
  id: string;
  campaignId: string;
  versionNumber: number;
  title: string | null;
  slug: string | null;
  excerpt: string | null;
  body: string | null;
  categoryId: string | null;
  coverImageUrl: string | null;
  startDate: string | null;
  endDate: string | null;
  ctaLink: string | null;
  metaTitle: string | null;
  metaDescription: string | null;
  canonicalTag: string | null;
  customCode: string | null;
  allowIndexing: boolean;
  authorUserId: string | null;
  authorEmail: string | null;
  changeNote: string | null;
  createdAt: string;
}

function mapListItem(d: CampaignListItemDto): CampaignListItem {
  return {
    id: d.id,
    title: d.title ?? '',
    slug: d.slug ?? '',
    excerpt: d.excerpt ?? '',
    categoryId: d.categoryId ?? null,
    coverImageUrl: d.coverImageUrl,
    startDate: d.startDate,
    endDate: d.endDate,
    status: campaignStatusFromNumber(d.status),
    publishedAt: d.publishedAt,
    updatedAt: d.updatedAt,
    languageId: d.languageId ?? '',
    groupId: d.groupId ?? '',
    variantLanguageCodes: d.variantLanguageCodes ?? [],
  };
}

function mapCampaign(d: CampaignDto): Campaign {
  return {
    id: d.id,
    title: d.title ?? '',
    slug: d.slug ?? '',
    excerpt: d.excerpt ?? '',
    body: d.body ?? '',
    categoryId: d.categoryId ?? null,
    coverImageUrl: d.coverImageUrl,
    startDate: d.startDate,
    endDate: d.endDate,
    ctaLink: d.ctaLink,
    metaTitle: d.metaTitle,
    metaDescription: d.metaDescription,
    canonicalTag: d.canonicalTag,
    customCode: d.customCode,
    keywords: d.keywords ?? null,
    allowIndexing: d.allowIndexing,
    status: campaignStatusFromNumber(d.status),
    publishedAt: d.publishedAt,
    publishDate: d.publishDate,
    createdAt: d.createdAt,
    updatedAt: d.updatedAt,
    languageId: d.languageId ?? '',
    groupId: d.groupId ?? '',
    variantLanguageCodes: d.variantLanguageCodes ?? [],
  };
}

function mapVersion(d: CampaignVersionDto): CampaignVersion {
  return {
    id: d.id,
    campaignId: d.campaignId,
    versionNumber: d.versionNumber,
    title: d.title ?? '',
    slug: d.slug ?? '',
    excerpt: d.excerpt ?? '',
    body: d.body ?? '',
    categoryId: d.categoryId ?? null,
    coverImageUrl: d.coverImageUrl,
    startDate: d.startDate,
    endDate: d.endDate,
    ctaLink: d.ctaLink,
    metaTitle: d.metaTitle,
    metaDescription: d.metaDescription,
    canonicalTag: d.canonicalTag,
    customCode: d.customCode,
    allowIndexing: d.allowIndexing,
    authorUserId: d.authorUserId,
    authorEmail: d.authorEmail,
    changeNote: d.changeNote,
    createdAt: d.createdAt,
  };
}

export interface CampaignListResult {
  items: CampaignListItem[];
  pagination: PaginationInfo | null;
  total: number;
}

export const campaignsApi = {
  async list(query: CampaignListQuery = {}): Promise<CampaignListResult> {
    const p = new URLSearchParams();
    if (query.search) p.set('search', query.search);
    if (query.categoryId) p.set('categoryId', query.categoryId);
    if (query.status) p.set('status', String(campaignStatusToNumber(query.status)));
    if (query.publishedOnly) p.set('publishedOnly', 'true');
    if (query.includeDeleted) p.set('includeDeleted', 'true');
    if (query.languageId) p.set('languageId', query.languageId);
    if (query.page) p.set('page', String(query.page));
    if (query.pageSize) p.set('pageSize', String(query.pageSize));
    if (query.sortBy) p.set('sortBy', query.sortBy);
    if (query.descending === false) p.set('descending', 'false');
    const qs = p.toString();
    const res = await http.get<ActionResponse<CampaignListItemDto>>(
      `/api/v1/campaigns${qs ? `?${qs}` : ''}`,
    );
    const { items, pagination } = unwrapList(res);
    return {
      items: items.map(mapListItem),
      pagination,
      total: pagination?.totalCount ?? items.length,
    };
  },

  async get(id: string): Promise<Campaign> {
    const res = await http.get<ActionResponse<CampaignDto>>(`/api/v1/campaigns/${id}`);
    return mapCampaign(unwrap(res));
  },

  /** POST → CampaignDtoActionResponse (dönen entity map'lenir). */
  async create(input: CampaignWriteInput): Promise<Campaign> {
    const res = await http.post<ActionResponse<CampaignDto>>('/api/v1/campaigns', input);
    return mapCampaign(unwrap(res));
  },

  async update(id: string, input: CampaignWriteInput): Promise<void> {
    const res = await http.put<ActionResponse<boolean>>(`/api/v1/campaigns/${id}`, input);
    unwrap(res);
  },

  async remove(id: string): Promise<void> {
    const res = await http.delete<ActionResponse<boolean>>(`/api/v1/campaigns/${id}`);
    unwrap(res);
  },

  async submitReview(id: string): Promise<void> {
    unwrap(await http.post<ActionResponse<boolean>>(`/api/v1/campaigns/${id}/submit-review`));
  },

  async approve(id: string, approve = true, note?: string): Promise<void> {
    const p = new URLSearchParams();
    p.set('approve', approve ? 'true' : 'false');
    if (note) p.set('note', note);
    unwrap(await http.post<ActionResponse<boolean>>(`/api/v1/campaigns/${id}/approve?${p.toString()}`));
  },

  async publish(id: string): Promise<void> {
    unwrap(await http.post<ActionResponse<boolean>>(`/api/v1/campaigns/${id}/publish`));
  },

  async archive(id: string): Promise<void> {
    unwrap(await http.post<ActionResponse<boolean>>(`/api/v1/campaigns/${id}/archive`));
  },

  async listVersions(id: string): Promise<CampaignVersion[]> {
    const res = await http.get<ActionResponse<CampaignVersionDto>>(
      `/api/v1/campaigns/${id}/versions`,
    );
    const { items } = unwrapList(res);
    return items.map(mapVersion);
  },

  async restoreVersion(id: string, versionNumber: number): Promise<void> {
    unwrap(
      await http.post<ActionResponse<boolean>>(
        `/api/v1/campaigns/${id}/versions/${versionNumber}/restore`,
      ),
    );
  },
};
